<template>
  <LandingHeader>
    <main>
      <HeroSection
        @scroll-to-section="scrollToSection"
      />

      <ShowcaseSection />

      <PreviewSection
        id="services"
        badge="Our Services"
        title="Student Services & Programs"
        subtitle="Comprehensive support for your academic journey and personal development."
        :items="servicesPreview"
        view-more-route="/services/student-welfare"
        view-more-text="Explore All Services"
      />

      <PreviewSection
        id="events"
        badge="Upcoming"
        title="Events & Activities"
        subtitle="Stay connected with campus life and upcoming opportunities."
        :items="eventsPreview"
        view-more-route="/events"
        view-more-text="View All Events"
      />

      <LostFoundSection />

      <ViolationsSection
        show-report-button
      />

      <StudentLoginSection />

      <AppointmentBookingSection />

      <ReferenceLookupSection />

      <FaqSection
        :faq-open="activeFaq"
        @toggle-faq="toggleFaq"
      />

      <ContactSection
        :form="formContact"
        :submitting="submitting.contact"
        @submit="submitContactForm"
      />
    </main>
  </LandingHeader>
</template>

<script>
// Layout
import LandingHeader from '@/components/landing/LandingHeader.vue'

// Sections
import HeroSection from '@/components/landing/sections/HeroSection.vue'
import ShowcaseSection from '@/components/landing/sections/ShowcaseSection.vue'
import PreviewSection from '@/components/landing/sections/PreviewSection.vue'
import LostFoundSection from '@/components/landing/sections/LostFoundSection.vue'
import FaqSection from '@/components/landing/sections/FaqSection.vue'
import ContactSection from '@/components/landing/sections/ContactSection.vue'
import ViolationsSection from '@/components/landing/sections/ViolationsSection.vue'
import StudentLoginSection from '@/components/landing/sections/StudentLoginSection.vue'
import AppointmentBookingSection from '@/components/landing/sections/AppointmentBookingSection.vue'
import ReferenceLookupSection from '@/components/landing/sections/ReferenceLookupSection.vue'
import MxImageUrl from '@/mixins/image-url'

// Mixins
import { MxLandingNavigation, MxLandingLostFound, MxLandingForms } from '@/mixins/landing'

// Services
import { LandingService } from '@/services/landing'

export default {
  name: 'LandingHome',

  metaInfo: () => ({
    title: 'Welcome to NC OSAS'
  }),

  components: {
    LandingHeader,
    HeroSection,
    ShowcaseSection,
    PreviewSection,
    LostFoundSection,
    FaqSection,
    ContactSection,
    ViolationsSection,
    StudentLoginSection,
    AppointmentBookingSection,
    ReferenceLookupSection
  },

  mixins: [
    MxLandingNavigation,
    MxLandingLostFound,
    MxLandingForms,
    MxImageUrl
  ],

  data () {
    return {
      submitting: {
        lostItem: false,
        counseling: false,
        jobPlacement: false,
        medical: false,
        contact: false,
        appointment: false
      },
      servicesPreview: [
        {
          icon: 'fas fa-hands-helping',
          title: 'Student Welfare',
          description: 'Guidance, counseling, and support services for student well-being.',
          color: 'green',
          route: '/services/student-welfare'
        },
        {
          icon: 'fas fa-chart-line',
          title: 'Student Development',
          description: 'Leadership programs and extracurricular activities for growth.',
          color: 'yellow',
          route: '/services/student-development'
        },
        {
          icon: 'fas fa-heartbeat',
          title: 'Health & Wellness',
          description: 'Medical services, mental health support, and wellness programs.',
          color: 'green',
          route: '/services/health-wellness'
        },
        {
          icon: 'fas fa-briefcase',
          title: 'Career Services',
          description: 'Job placement assistance and career development opportunities.',
          color: 'yellow',
          route: '/services/career'
        }
      ],
      eventsPreview: this.getDefaultEventsPreview()
    }
  },

  watch: {
    '$route.fullPath' () {
      this.handleInitialSectionNavigation()
    }
  },

  mounted () {
    this.handleInitialSectionNavigation()
    this.fetchLandingHighlights()
  },

  methods: {
    resolveInitialSectionId () {
      const sectionPathMap = {
        '/home/faqs': 'faq',
        '/home/contact': 'contact',
        '/home/book-appointment': 'book-appointment'
      }

      if (sectionPathMap[this.$route.path]) {
        return sectionPathMap[this.$route.path]
      }

      // Legacy hash support for existing shared links.
      return this.$route.hash ? this.$route.hash.replace('#', '') : null
    },

    handleInitialSectionNavigation () {
      const sectionId = this.resolveInitialSectionId()

      if (!sectionId) {
        return
      }

      // Delay to ensure DOM is fully rendered.
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToSection(sectionId)
        }, 100)
      })
    },

    getDefaultEventsPreview () {
      return [
        {
          image: require('@/assets/images/nccampus.png'),
          title: 'Campus Events',
          description: 'Stay updated with the latest campus activities and events.',
          route: '/events'
        },
        {
          image: require('@/assets/images/nccampus.png'),
          title: 'Community Programs',
          description: 'Engage with community outreach and institutional programs.',
          route: '/services/community'
        },
        {
          image: require('@/assets/images/nccampus.png'),
          title: 'Policies & Forms',
          description: 'Access important policies, guidelines, and downloadable forms.',
          route: '/policies'
        }
      ]
    },

    async fetchLandingHighlights () {
      try {
        const [{ data: eventData }, { data: announcementData }] = await Promise.all([
          LandingService.getEvents({ per_page: 2 }),
          LandingService.getAnnouncements({ per_page: 1 })
        ])

        const eventCards = (eventData?.items || []).slice(0, 2).map(item => ({
          image: item.image_url ? this.resolveStoragePath(item.image_url) : require('@/assets/images/nccampus.png'),
          title: item.title,
          description: item.description || 'Campus event update',
          route: '/events'
        }))

        const announcementCards = (announcementData?.items || []).slice(0, 1).map(item => ({
          image: item.image_url ? this.resolveStoragePath(item.image_url) : require('@/assets/images/nccampus.png'),
          title: item.title,
          description: item.content || 'Latest announcement from OSAS',
          route: '/events'
        }))

        const cards = [...eventCards, ...announcementCards]
        if (cards.length > 0) {
          this.eventsPreview = cards
        }
      } catch {
        this.eventsPreview = this.getDefaultEventsPreview()
      }
    },

    async submitContactForm () {
      if (!this.validateContactForm()) return

      this.submitting.contact = true
      try {
        this.$bvToast.toast('Message captured. Please use the listed OSAS emails/phone for immediate response.', {
          title: 'Notice',
          variant: 'success',
          solid: true
        })
        this.resetContactForm()
      } finally {
        this.submitting.contact = false
      }
    }
  }
}
</script>
